import { useState, useRef } from "react";
import { MdOutlineFeedback } from "react-icons/md";
import { CheckCircleIcon, ArrowLeftIcon } from "@heroicons/react/24/outline";

const TYPES = [
  {
    key: "denuncia",
    label: "Denúncia",
    description: "Reporte irregularidades ou ilícitos praticados por agentes públicos ou na prestação de serviços.",
    cardCls: "border-red-200 bg-red-50 hover:bg-red-100",
    textCls: "text-red-800",
    activeCls: "border-red-500 bg-red-100 ring-2 ring-red-400",
    badgeCls: "bg-red-600 text-white",
    placeholder: "Descreva a irregularidade ou ilícito que deseja denunciar, com o maior número de detalhes possível...",
  },
  {
    key: "reclamacao",
    label: "Reclamação",
    description: "Manifeste insatisfação com serviços prestados, omissão de providências ou irregularidades.",
    cardCls: "border-orange-200 bg-orange-50 hover:bg-orange-100",
    textCls: "text-orange-800",
    activeCls: "border-orange-500 bg-orange-100 ring-2 ring-orange-400",
    badgeCls: "bg-orange-500 text-white",
    placeholder: "Descreva sua reclamação em detalhes, incluindo datas, locais e pessoas envolvidas quando possível...",
  },
  {
    key: "sugestao",
    label: "Sugestão",
    description: "Contribua com ideias para a melhoria dos serviços públicos municipais.",
    cardCls: "border-green-200 bg-green-50 hover:bg-green-100",
    textCls: "text-green-800",
    activeCls: "border-green-500 bg-green-100 ring-2 ring-green-400",
    badgeCls: "bg-green-600 text-white",
    placeholder: "Compartilhe sua sugestão de melhoria para os serviços ou estruturas públicas municipais...",
  },
  {
    key: "elogio",
    label: "Elogio",
    description: "Reconheça o bom atendimento ou destaque servidores e serviços que merecem elogio.",
    cardCls: "border-blue-200 bg-blue-50 hover:bg-blue-100",
    textCls: "text-blue-800",
    activeCls: "border-blue-500 bg-blue-100 ring-2 ring-blue-400",
    badgeCls: "bg-blue-600 text-white",
    placeholder: "Descreva o que motivou seu elogio, incluindo o nome do servidor ou serviço quando souber...",
  },
];

const EMPTY_FORM = {
  nome: "",
  email: "",
  cpf: "",
  telefone: "",
  descricao: "",
  sigilo: false,
  termos: false,
};

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const inputCls = (error) =>
  `w-full border rounded-xl px-4 py-2.5 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition ${
    error ? "border-red-400 bg-red-50" : "border-gray-300 bg-white"
  }`;

const Field = ({ label, error, required, children }) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-1">
      {label} {required && <span className="text-red-500">*</span>}
    </label>
    {children}
    {error && <p className="text-xs text-red-600 mt-1">{error}</p>}
  </div>
);

const generateProtocol = (prefix) => {
  const year = new Date().getFullYear();
  const num = String(Math.floor(10000 + Math.random() * 90000));
  return `OUV-${prefix.toUpperCase()}-${year}-${num}`;
};

const Ouvidoria = () => {
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [protocol, setProtocol] = useState("");
  const formRef = useRef(null);

  const selectedType = TYPES.find((t) => t.key === selected);

  const handleSelectType = (key) => {
    setSelected(key);
    setErrors({});
    setTimeout(() => formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 50);
  };

  const set = (field) => (e) => {
    const value = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const validate = () => {
    const errs = {};
    if (!form.nome.trim() || form.nome.trim().length < 3)
      errs.nome = "Informe o nome completo (mínimo 3 caracteres).";
    if (!form.email.trim()) errs.email = "E-mail é obrigatório.";
    else if (!emailRegex.test(form.email)) errs.email = "Informe um e-mail válido.";
    if (!form.descricao.trim() || form.descricao.trim().length < 20)
      errs.descricao = "Descreva a manifestação com pelo menos 20 caracteres.";
    if (!form.termos) errs.termos = "É necessário declarar a veracidade das informações.";
    return errs;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) {
      setErrors(errs);
      return;
    }
    setProtocol(generateProtocol(selected));
    setSubmitted(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleReset = () => {
    setSubmitted(false);
    setSelected(null);
    setForm(EMPTY_FORM);
    setErrors({});
    setProtocol("");
  };

  if (submitted && selectedType) {
    return (
      <div className="p-6 md:p-8 max-w-2xl mx-auto space-y-6">
        <div className="bg-green-50 border border-green-200 rounded-2xl p-8 text-center space-y-4">
          <CheckCircleIcon className="h-14 w-14 text-green-600 mx-auto" />
          <div>
            <span className={`inline-block px-3 py-1 rounded-full text-xs font-bold mb-2 ${selectedType.badgeCls}`}>
              {selectedType.label}
            </span>
            <h2 className="text-xl font-bold text-green-900">Manifestação Registrada!</h2>
            <p className="text-sm text-green-800 mt-1">
              Sua {selectedType.label.toLowerCase()} foi recebida pela Ouvidoria Municipal com sucesso.
            </p>
          </div>
          <div className="bg-white border border-green-300 rounded-xl p-4 inline-block">
            <p className="text-xs text-gray-500 uppercase tracking-wider font-semibold">Número de Protocolo</p>
            <p className="text-xl font-bold text-blue-950 mt-1 tracking-widest">{protocol}</p>
          </div>
          <div className="text-sm text-green-800 bg-green-100 rounded-xl p-4 text-left space-y-1">
            <p><strong>Próximos passos:</strong></p>
            <p>• Guarde o número de protocolo para acompanhar sua manifestação.</p>
            {!form.sigilo && (
              <p>• Você receberá a resposta no e-mail <strong>{form.email}</strong> em até <strong>30 dias úteis</strong>.</p>
            )}
            {form.sigilo && (
              <p>• Sua identidade está protegida. A resposta seguirá tratamento sigiloso.</p>
            )}
            <p>• Em caso de dúvidas, entre em contato pelo telefone <strong>(84) 99999-9999</strong>.</p>
          </div>
        </div>
        <button
          onClick={handleReset}
          className="inline-flex items-center gap-2 text-sm text-blue-700 hover:underline"
        >
          <ArrowLeftIcon className="h-4 w-4" /> Fazer nova manifestação
        </button>
      </div>
    );
  }

  return (
    <div className="p-6 md:p-8 space-y-6 max-w-4xl mx-auto">

      {/* Header */}
      <div className="bg-blue-50 border border-blue-100 rounded-xl p-5 flex items-start gap-4">
        <span className="text-blue-700 text-3xl mt-0.5 shrink-0">
          <MdOutlineFeedback />
        </span>
        <div>
          <h2 className="text-xl font-bold text-blue-950 mb-1">Ouvidoria Municipal</h2>
          <p className="text-blue-900 text-sm leading-relaxed">
            A Ouvidoria é um canal direto entre o cidadão e a administração municipal.
            Selecione o tipo de manifestação e preencha o formulário abaixo. Todas as
            manifestações são tratadas com sigilo e respondidas em até{" "}
            <strong>30 dias úteis</strong>, conforme a Lei de Acesso à Informação.
          </p>
        </div>
      </div>

      {/* Type selector */}
      <div>
        <p className="text-sm font-semibold text-gray-700 mb-3">
          1. Selecione o tipo de manifestação:
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {TYPES.map((type) => (
            <button
              key={type.key}
              type="button"
              onClick={() => handleSelectType(type.key)}
              className={`flex flex-col gap-2 border rounded-xl p-5 text-left transition ${
                selected === type.key ? type.activeCls : type.cardCls
              }`}
            >
              <span className={`text-base font-bold ${type.textCls}`}>{type.label}</span>
              <p className={`text-sm leading-relaxed ${type.textCls} opacity-80`}>
                {type.description}
              </p>
            </button>
          ))}
        </div>
      </div>

      {/* Form */}
      {selected && selectedType && (
        <div ref={formRef} className="space-y-4">
          <p className="text-sm font-semibold text-gray-700">
            2. Preencha os dados da sua{" "}
            <span className={`font-bold ${selectedType.textCls}`}>
              {selectedType.label}
            </span>:
          </p>

          {/* Error summary */}
          {Object.keys(errors).length > 0 && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-4">
              <p className="text-sm font-semibold text-red-800 mb-1">Corrija os erros abaixo:</p>
              <ul className="text-sm text-red-700 list-disc list-inside space-y-0.5">
                {Object.values(errors).map((msg, i) => <li key={i}>{msg}</li>)}
              </ul>
            </div>
          )}

          <form onSubmit={handleSubmit} noValidate className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 space-y-4">

            {/* Type badge */}
            <div className="flex items-center gap-2 pb-3 border-b border-gray-100">
              <span className={`px-3 py-1 rounded-full text-xs font-bold ${selectedType.badgeCls}`}>
                {selectedType.label}
              </span>
              <button
                type="button"
                onClick={() => setSelected(null)}
                className="text-xs text-gray-400 hover:text-gray-600 underline ml-auto"
              >
                Alterar tipo
              </button>
            </div>

            {/* Identification */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Nome completo" required error={errors.nome}>
                <input
                  type="text"
                  value={form.nome}
                  onChange={set("nome")}
                  placeholder="Seu nome completo"
                  className={inputCls(errors.nome)}
                />
              </Field>
              <Field label="E-mail" required error={errors.email}>
                <input
                  type="email"
                  value={form.email}
                  onChange={set("email")}
                  placeholder="seu@email.com.br"
                  className={inputCls(errors.email)}
                />
              </Field>
              <Field label="CPF" error={errors.cpf}>
                <input
                  type="text"
                  value={form.cpf}
                  onChange={set("cpf")}
                  placeholder="000.000.000-00 (opcional)"
                  maxLength={14}
                  className={inputCls(errors.cpf)}
                />
              </Field>
              <Field label="Telefone" error={errors.telefone}>
                <input
                  type="tel"
                  value={form.telefone}
                  onChange={set("telefone")}
                  placeholder="(84) 99999-9999 (opcional)"
                  className={inputCls(errors.telefone)}
                />
              </Field>
            </div>

            {/* Description */}
            <Field label="Descrição da manifestação" required error={errors.descricao}>
              <textarea
                value={form.descricao}
                onChange={set("descricao")}
                placeholder={selectedType.placeholder}
                rows={5}
                maxLength={4000}
                className={`${inputCls(errors.descricao)} resize-none`}
              />
              <p className="text-xs text-gray-400 mt-1 text-right">{form.descricao.length}/4000</p>
            </Field>

            {/* Options */}
            <div className="space-y-3 pt-2">
              <label className="flex items-start gap-3 cursor-pointer group">
                <input
                  type="checkbox"
                  checked={form.sigilo}
                  onChange={set("sigilo")}
                  className="mt-0.5 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <div>
                  <p className="text-sm font-medium text-gray-700 group-hover:text-blue-800">
                    Manter sigilo do solicitante
                  </p>
                  <p className="text-xs text-gray-400">
                    Seus dados pessoais não serão divulgados no tratamento desta manifestação.
                  </p>
                </div>
              </label>

              <label className={`flex items-start gap-3 cursor-pointer group p-3 rounded-xl border ${errors.termos ? "border-red-300 bg-red-50" : "border-gray-100 bg-gray-50"}`}>
                <input
                  type="checkbox"
                  checked={form.termos}
                  onChange={set("termos")}
                  className="mt-0.5 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <div>
                  <p className="text-sm font-medium text-gray-700 group-hover:text-blue-800">
                    Declaro que as informações prestadas são verdadeiras{" "}
                    <span className="text-red-500">*</span>
                  </p>
                  <p className="text-xs text-gray-400 mt-0.5">
                    Conforme a legislação vigente, a prestação de informações falsas pode implicar responsabilização.
                  </p>
                </div>
              </label>
              {errors.termos && <p className="text-xs text-red-600">{errors.termos}</p>}
            </div>

            {/* Submit */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 pt-2">
              <p className="text-xs text-gray-400">
                Campos marcados com <span className="text-red-500 font-bold">*</span> são obrigatórios.
              </p>
              <button
                type="submit"
                className="shrink-0 bg-blue-900 text-white font-semibold py-3 px-8 rounded-xl hover:bg-blue-700 active:bg-blue-950 transition focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              >
                Enviar Manifestação
              </button>
            </div>

          </form>
        </div>
      )}

      {/* Footer note */}
      <div className="p-4 bg-gray-50 border border-gray-200 rounded-xl text-sm text-gray-600">
        Caso prefira o atendimento presencial, dirija-se à sede da Prefeitura de Serrinha,
        localizada na <strong>Rua Manoel Joaquim de Souza, 150, Centro — Serrinha/RN</strong>,
        de segunda a sexta-feira, das 08h às 13h.
      </div>

    </div>
  );
};

export default Ouvidoria;
